import { Center, Flex, Spinner, Stack } from "@chakra-ui/react";
import { captureException } from "@sentry/react";
import React, { useEffect, useState } from "react";
import { useNavigate, useRouteError } from "react-router-dom";
import { AppBarBase } from "../components/AppBar/AppBar";
import { FallbackFullPage } from "../components/Fallback/Fallback";
import { useSessionStorageState } from "../hooks/useStorageState";

function isDynamicModuleError(error: unknown) {
  if (error instanceof Error) {
    return error.message.includes("dynamically imported module");
  }
}

function AppContainer({ children }: { children: React.ReactNode }) {
  return (
    <Flex direction="column" w="100vw" h="100vh" position={"relative"}>
      <AppBarBase homeUrl={"/"} />
      <Center h="100%" w="100%">
        {children}
      </Center>
    </Flex>
  );
}

export function RouterErrorBoundary() {
  const error = useRouteError();
  const [eventId] = useState(captureException(error));

  if (isDynamicModuleError(error)) {
    return <AttemptRefresh />;
  }

  return (
    <FallbackFullPage
      error={error as Error}
      eventId={eventId}
      componentStack=""
      resetError={() => {}}
    />
  );
}

export function AttemptRefresh() {
  useAttemptRefresh();
  return (
    <AppContainer>
      <Stack align="center" gap={6}>
        <Spinner size={"xl"} />
      </Stack>
    </AppContainer>
  );
}

const timeoutMs = 500;
const maxAttempts = 2;
const ttl = 30_000;

function useAttemptRefresh() {
  const navigate = useNavigate();
  const [now] = useState(Date.now());
  const [, setState] = useSessionStorageState<number[]>(
    "attemptRefreshState",
    []
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setState((cur) => {
        return [...cur, now];
      });
      window.location.reload();
    }, timeoutMs);

    setState((cur) => {
      const next = cur.filter((t) => {
        const delta = now - t;
        return delta < ttl;
      });

      if (next.length >= maxAttempts) {
        clearTimeout(timeoutId);
        navigate("/");
        return [];
      }
      return next;
    });
  }, []);
}
